'use client'

import { AnalyticsProvider } from '@components/analytics-provider'
import { ArtBlocksProvider } from '@components/artblocks-provider'
import { NavProvider } from '@components/nav/nav-provider'
import { PrivyProvider } from '@components/privy-provider'
import { PurchaseTrackingManagerMachineProvider } from '@components/purchase-tracking-manager-machine-context/purchase-tracking-manager-machine-provider'
import { ReservoirProvider } from '@components/reservoir-provider'
import { UrqlProvider } from '@components/urql-provider'
import { createConfig, WagmiProvider } from '@privy-io/wagmi'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { TooltipProvider } from 'design-configuration/components'
import { SessionProvider } from 'next-auth/react'
import React, { Suspense } from 'react'
import { fallback, http, custom } from 'viem'
import { mainnet, sepolia } from 'wagmi/chains'
import { ArtBlocksAuthProvider } from './auth/artblocks-auth-provider'
import { FeatureToggleProvider } from './auth/feature-toggle-provider'
import { ExcessSettlementFundsManagerMachineContextProvider } from '@components/excess-settlement-funds-manager-context/excess-settlement-funds-manager-context-provider'
import { Toaster } from '@components/toaster'

// wallet RPC if present, otherwise omit
const walletProvider = typeof window !== 'undefined' && window.ethereum ? custom(window.ethereum) : undefined

export const wagmiConfig = createConfig({
  chains: process.env.NEXT_PUBLIC_CURRENT_ENV === 'prod' ? [mainnet] : [sepolia],
  ssr: true,
  // @ts-expect-error - TODO: This works fine, so likely a type bug in wagmi/privy
  transports:
    process.env.NEXT_PUBLIC_CURRENT_ENV === 'prod'
      ? {
          [mainnet.id]: fallback(
            [
              // prefer private provider
              http(process.env.NEXT_PUBLIC_JSON_RPC_PROVIDER_URL ?? '', {
                retryCount: 2, // number of times to retry before falling back to next provider
                retryDelay: 200 // base delay, with exponential backoff
              }),
              // private provider fallback if available
              process.env.NEXT_PUBLIC_JSON_RPC_PROVIDER_URL_FALLBACK
                ? http(process.env.NEXT_PUBLIC_JSON_RPC_PROVIDER_URL_FALLBACK ?? '', {
                    retryCount: 2, // number of times to retry before falling back to next provider
                    retryDelay: 200 // base delay, with exponential backoff
                  })
                : undefined,
              // fallback back to public provider
              http(undefined, {
                retryCount: 2, // number of times to retry before falling back to next provider
                retryDelay: 200 // base delay, with exponential backoff
              }),
              // if wallet is present, use it as last resort
              walletProvider ? walletProvider : undefined
            ].filter((transport) => transport !== undefined),
            {
              // overall fallback chain will retry 3 times, with additional exponential backoff
              rank: false, // follow strict order of providers
              retryCount: 3, // entire fallback chain will retry 2 times
              retryDelay: 200 // base delay, with exponential backoff
            }
          )
        }
      : {
          [sepolia.id]: fallback(
            [
              // prefer private provider
              http(process.env.NEXT_PUBLIC_SEPOLIA_JSON_RPC_PROVIDER_URL ?? '', {
                retryCount: 2, // number of times to retry before falling back to next provider
                retryDelay: 200 // base delay, with exponential backoff
              }),
              // private provider fallback if available
              process.env.NEXT_PUBLIC_SEPOLIA_JSON_RPC_PROVIDER_URL_FALLBACK
                ? http(process.env.NEXT_PUBLIC_SEPOLIA_JSON_RPC_PROVIDER_URL_FALLBACK ?? '', {
                    retryCount: 2, // number of times to retry before falling back to next provider
                    retryDelay: 200 // base delay, with exponential backoff
                  })
                : undefined,
              // fallback back to public provider
              http(undefined, {
                retryCount: 2, // number of times to retry before falling back to next provider
                retryDelay: 200 // base delay, with exponential backoff
              }),
              // if wallet is present, use it as last resort
              walletProvider ? walletProvider : undefined
            ].filter((transport) => transport !== undefined),
            {
              rank: false, // follow strict order of providers
              retryCount: 3, // entire fallback chain will retry 3 times
              retryDelay: 200
            }
          )
        }
})

const queryClient = new QueryClient()

export default function GlobalProviders({
  children,
  globalFlags
}: {
  children: React.ReactNode
  globalFlags: string[]
}): React.ReactElement {
  return (
    <PrivyProvider>
      <QueryClientProvider client={queryClient}>
        <WagmiProvider config={wagmiConfig}>
          <ReservoirProvider>
            <SessionProvider>
              <ArtBlocksAuthProvider>
                <UrqlProvider>
                  <AnalyticsProvider>
                    <ArtBlocksProvider>
                      <ExcessSettlementFundsManagerMachineContextProvider>
                        <PurchaseTrackingManagerMachineProvider>
                          <FeatureToggleProvider globalFlags={globalFlags}>
                            <TooltipProvider>
                              <Toaster />
                              <Suspense>
                                <NavProvider>{children}</NavProvider>
                              </Suspense>
                            </TooltipProvider>
                          </FeatureToggleProvider>
                        </PurchaseTrackingManagerMachineProvider>
                      </ExcessSettlementFundsManagerMachineContextProvider>
                    </ArtBlocksProvider>
                  </AnalyticsProvider>
                </UrqlProvider>
              </ArtBlocksAuthProvider>
            </SessionProvider>
          </ReservoirProvider>
        </WagmiProvider>
      </QueryClientProvider>
    </PrivyProvider>
  )
}
